import { type MouseEvent, type ReactElement, type ReactNode } from 'react'

import { useIsMobile } from '@/hooks/useMobile'

import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerTitle,
  OpenOnlyTrigger,
  useDrawerTapGuard,
} from '@/components/ui/drawer'

interface ResponsivePopoverProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  trigger: ReactElement<{ onClick?: (event: MouseEvent) => void }>
  children: ReactNode
  contentClassName?: string
  align?: 'start' | 'center' | 'end'
  sideOffset?: number
}

function ResponsivePopover({
  open,
  onOpenChange,
  trigger,
  children,
  contentClassName,
  align,
  sideOffset,
}: ResponsivePopoverProps) {
  const isMobile = useIsMobile()
  const setOpen = useDrawerTapGuard(onOpenChange, open)

  if (isMobile) {
    return (
      <>
        {/* No DrawerTrigger: a controlled trigger toggles on the tap that also
            hits the overlay, so the sheet never settles (MOL-41). */}
        <OpenOnlyTrigger onOpen={() => setOpen(true)}>{trigger}</OpenOnlyTrigger>
        <Drawer open={open} onOpenChange={setOpen} direction="bottom" repositionInputs={false}>
          <DrawerContent onOpenAutoFocus={(event) => event.preventDefault()}>
            <DrawerTitle className="sr-only">Options</DrawerTitle>
            <DrawerDescription className="sr-only">
              Select an option from the list.
            </DrawerDescription>
            <div className="max-h-[70vh] overflow-y-auto p-4">{children}</div>
          </DrawerContent>
        </Drawer>
      </>
    )
  }

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        className={contentClassName}
        align={align}
        sideOffset={sideOffset}
      >
        {children}
      </PopoverContent>
    </Popover>
  )
}

export { ResponsivePopover }
export type { ResponsivePopoverProps }
